import React, { useState } from 'react';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import { OrderObject } from 'common/types';
import { Typography, Box, Button } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useTranslation } from 'services/localization/useTranslation';
import { DTAOrderStatuses } from 'common/types';
import { Callable } from 'common/frontend/callable';
import Spinner from 'components/Spinner';
import BackdropDialog from 'common/components/BackdropDialog';

import {
	hasSkidataConfirmationNumber,
	hasMissingKeycardNumbers,
} from 'common/utils/liftTicketUtils';
import Banner from 'components/Banner';

interface OwnProps {
	rental: OrderObject;
}

type Props = OwnProps;

export const SkidataInformation = ({ ...props }: Props) => {
	const { rental } = props;
	const { rentalInfo, products } = rental;
	const classes = useStyles();
	const { t } = useTranslation();

	const hasSkidataConfirmation = hasSkidataConfirmationNumber(rentalInfo);
	const isMissingKeycardNumbers = hasMissingKeycardNumbers(products);

	const { skidataProps } = rentalInfo;
	const orderStatus = skidataProps?.DTAOrderStatus;

	const hasErrors = !hasSkidataConfirmation || isMissingKeycardNumbers;
	const isCancelled = skidataProps?.DTAOrderStatus === DTAOrderStatuses.CANCELLED;

	const [expanded, setExpanded] = useState(hasErrors);
	const [loading, setLoading] = useState(false);
	const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

	const cancelSkidataOrder = async () => {
		if (!skidataProps?.confirmationNumber || !skidataProps.DTAOrderStatus || isCancelled) {
			return;
		}
		setLoading(true);
		await Callable.skidata.orders.cancel({
			shopId: rentalInfo.shopId,
			orderId: rentalInfo.id,
			skidataOrderStatus: skidataProps.DTAOrderStatus,
			skidataOrderId: skidataProps.orderId,
		});
		setLoading(false);
	};

	const renderCompletedStatus = () => (
		<Banner
			variant="success"
			title={t('skidata.orderRegistered', 'Order was registered to SKIDATA.')}
		/>
	);

	const renderMissingKeycardsStatus = () => (
		<Banner
			variant="warning"
			title={t('skidata.orderMissingKeycardsAlert', 'Order missing some keycards.')}
		/>
	);

	const renderMissingPhotosStatus = () => (
		<Banner
			variant="warning"
			title={t('skidata.orderMissingPhotosAlert', 'Order missing some photos.')}
		/>
	);

	const renderOrderNotFound = () => (
		<Banner
			variant="warning"
			title={t(
				'skidata.orderNotRegisteredAlert',
				'Failed to create order. Create order manually in SKIDATA.',
			)}
		/>
	);

	const renderCancelledStatus = () => (
		<Banner variant="info" title={t('skidata.orderCancelled', 'Order was cancelled in SKIDATA.')} />
	);

	const renderOrderStatus = () => {
		const {
			BOOKED_AND_TRANSFERRED,
			RESERVED,
			MISSING_KEYCARDS,
			MISSING_PHOTOS,
			CANCELLED,
		} = DTAOrderStatuses;
		switch (orderStatus) {
			case BOOKED_AND_TRANSFERRED:
			case RESERVED:
				return renderCompletedStatus();
			case MISSING_KEYCARDS:
				return renderMissingKeycardsStatus();
			case MISSING_PHOTOS:
				return renderMissingPhotosStatus();
			case CANCELLED:
				return renderCancelledStatus();
			default:
				return renderOrderNotFound();
		}
	};

	return (
		<div>
			<Box className={classes.inline}>
				<Typography variant="subtitle1" style={{ flexGrow: 1 }}>
					{t('skidata.header')}{' '}
					{hasSkidataConfirmation && (
						<>
							<span>{'ID '}</span>
							<span>{rentalInfo.skidataProps!.confirmationNumber}</span>
						</>
					)}
				</Typography>

				{expanded ? (
					<KeyboardArrowUpIcon onClick={() => setExpanded(!expanded)} />
				) : (
					<KeyboardArrowDownIcon onClick={() => setExpanded(!expanded)} />
				)}
			</Box>
			{expanded ? (
				loading ? (
					<Spinner relative={true} size={20} margin={10} />
				) : (
					<>
						{renderOrderStatus()}
						{!isCancelled && (
							<Box textAlign="center">
								<Button size="small" color="error" onClick={() => setIsCancelModalOpen(true)}>
									{t('skidata.cancelOrder', 'Cancel order in Skidata')}
								</Button>
							</Box>
						)}
					</>
				)
			) : null}
			<BackdropDialog
				isOpen={isCancelModalOpen}
				onClose={() => setIsCancelModalOpen(false)}
				variant="danger"
				position="center"
				label={`${t('skidata.cancelOrder', 'Cancel order in Skidata')}?`}
				body={
					<>
						<Typography variant="body1">
							{t('skidata.cancelOrderModalBody1', 'This action cannot be undone.')}
						</Typography>
						<Typography variant="body1" mt={2}>
							{t(
								'skidata.cancelOrderModalBody2',
								'Note: cancelling the order does not refund the payment. The payment needs to be refunded separately.',
							)}
						</Typography>
					</>
				}
				confirm={{
					label: t('common:actions.confirm'),
					callback: cancelSkidataOrder,
					closeAfterDone: true,
					loading: loading,
				}}
				cancel={{
					label: t('common:actions.no'),
					callback: () => setIsCancelModalOpen(false),
				}}
			/>
		</div>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		inline: {
			display: 'flex',
			alignItems: 'center',
		},
	}),
);

export default SkidataInformation;
